import { getUnlocAccessToken } from './dataverseService';
import { getActiveUnlocProjectId, getUnlocApimBaseUrl } from '../config/unlocApi';
import { normalizeMobileNumber } from '../config/keyRequest';

const DEFAULT_PAGE_LIMIT = 2500;
const MAX_PAGES_FILTERED = 20;
/** Broad project-wide fetches without lock/phone — avoid loading tens of thousands of rows. */
const MAX_PAGES_BROAD = 3;

async function readApimError(response, fallback) {
  try {
    const text = await response.text();
    if (!text) {
      return `${fallback} (${response.status}).`;
    }

    try {
      const json = JSON.parse(text);
      return json.error || json.message || json.error_description || json.detail || text;
    } catch {
      return text;
    }
  } catch {
    return `${fallback} (${response.status}).`;
  }
}

function requireProjectId() {
  const projectId = getActiveUnlocProjectId();
  if (!projectId) {
    throw new Error('Unloc-prosjekt er ikke satt for denne kommunen.');
  }
  return projectId;
}

async function unlocFetch(path, { query, fallback = 'Unloc-kall feilet' } = {}) {
  const token = await getUnlocAccessToken();
  if (!token) {
    return null;
  }

  const params = new URLSearchParams();
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value));
      }
    }
  }

  const qs = params.toString();
  const url = `${getUnlocApimBaseUrl()}${path}${qs ? `?${qs}` : ''}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` }
  });

  if (!response.ok) {
    throw new Error(await readApimError(response, fallback));
  }

  const text = await response.text();
  if (!text) {
    return { events: [] };
  }

  try {
    return JSON.parse(text);
  } catch {
    return { events: [] };
  }
}

function accessLogPath() {
  return `/v2/projects/${encodeURIComponent(requireProjectId())}/logs/access-events`;
}

function sameMobile(a, b) {
  const left = normalizeMobileNumber(a) || String(a || '').trim();
  const right = normalizeMobileNumber(b) || String(b || '').trim();
  if (!left || !right) {
    return false;
  }
  return left === right || left.replace(/^\+/, '') === right.replace(/^\+/, '');
}

function extractAccessEvents(payload) {
  if (!payload) {
    return [];
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload.events)) {
    return payload.events;
  }
  if (Array.isArray(payload.value)) {
    return payload.value;
  }
  if (Array.isArray(payload.items)) {
    return payload.items;
  }
  if (Array.isArray(payload.data)) {
    return payload.data;
  }
  if (Array.isArray(payload?.data?.events)) {
    return payload.data.events;
  }
  return [];
}

function eventTimeMs(event) {
  const raw =
    event?.timestamp || event?.created || event?.createdAt || event?.time || event?.occurredAt;
  if (!raw) {
    return null;
  }
  const ms = new Date(raw).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function filterEventsByRange(events, fromMs, toMs) {
  if (fromMs == null && toMs == null) {
    return events;
  }
  return events.filter((event) => {
    const at = eventTimeMs(event);
    if (at == null) {
      return true;
    }
    if (fromMs != null && at < fromMs) {
      return false;
    }
    if (toMs != null && at > toMs) {
      return false;
    }
    return true;
  });
}

/** True when every dated event on the page is older than from (Unloc pages newest-first). */
function pageEntirelyBeforeFrom(batch, fromMs) {
  if (fromMs == null || !batch.length) {
    return false;
  }
  let sawDated = false;
  for (const event of batch) {
    const at = eventTimeMs(event);
    if (at == null) {
      continue;
    }
    sawDated = true;
    if (at >= fromMs) {
      return false;
    }
  }
  return sawDated;
}

async function fetchAccessEventsPaged({
  lockId,
  appUserId,
  limit = DEFAULT_PAGE_LIMIT,
  from,
  to,
  maxPages
} = {}) {
  const fromMs = from ? new Date(from).getTime() : null;
  const toMs = to ? new Date(to).getTime() : null;
  const fromMsSafe = fromMs != null && !Number.isNaN(fromMs) ? fromMs : null;
  const toMsSafe = toMs != null && !Number.isNaN(toMs) ? toMs : null;

  const scoped = Boolean(lockId || appUserId);
  const pageCap = maxPages ?? (scoped ? MAX_PAGES_FILTERED : MAX_PAGES_BROAD);

  const events = [];
  let startAfterId;

  for (let page = 0; page < pageCap; page += 1) {
    const result = await unlocFetch(accessLogPath(), {
      query: {
        lockId: lockId || undefined,
        appUserId: appUserId || undefined,
        excludeSubprojectEvents: true,
        limit,
        startAfterId
      },
      fallback: 'Kunne ikke hente hendelseslogg fra Unloc'
    });

    if (!result) {
      return null;
    }

    const batch = extractAccessEvents(result);
    if (!batch.length) {
      break;
    }

    // Newest-first: once a full page is older than from, further pages are older too.
    if (pageEntirelyBeforeFrom(batch, fromMsSafe)) {
      break;
    }

    events.push(...filterEventsByRange(batch, fromMsSafe, toMsSafe));

    const nextId =
      result.startAfterId ||
      result.nextStartAfterId ||
      (batch.length ? batch[batch.length - 1]?.id : null);
    if (batch.length < limit || !nextId || nextId === startAfterId) {
      break;
    }

    startAfterId = nextId;
  }

  return { events };
}

/**
 * Live Unloc Access Events (siste 60 dager). Optional lockId / appUserId / from / to.
 * Unloc/APIM returnerer ofte 500 når appUserId sendes som query — da henter vi
 * bredere (evt. kun lockId) og filtrerer telefonnummeret lokalt.
 */
export async function listUnlocAccessEvents({
  lockId,
  appUserId,
  limit = DEFAULT_PAGE_LIMIT,
  from,
  to,
  maxPages
} = {}) {
  const mobile = appUserId ? normalizeMobileNumber(appUserId) || String(appUserId).trim() : '';
  const mobileWithoutPlus = mobile.startsWith('+') ? mobile.slice(1) : mobile;
  const pageOpts = { lockId, limit, from, to, maxPages };

  if (!mobile) {
    return fetchAccessEventsPaged(pageOpts);
  }

  const appUserIdVariants = [...new Set([mobile, mobileWithoutPlus].filter(Boolean))];

  for (const variant of appUserIdVariants) {
    try {
      const result = await fetchAccessEventsPaged({ ...pageOpts, appUserId: variant });
      if (result === null) {
        return null; // mangler Unloc-token
      }
      // Tom liste med 200 er ofte feil telefonformat — ikke returner; fall til klientfilter.
      if ((result.events || []).length > 0) {
        return result;
      }
    } catch {
      // Unloc/APIM 500 på appUserId — prøv neste variant / klientfilter
    }
  }

  const broader = await fetchAccessEventsPaged(pageOpts);
  if (!broader) {
    return null;
  }

  return {
    events: (broader.events || []).filter((event) =>
      sameMobile(event.actorId || event.appUserId, mobile)
    )
  };
}
